/* eslint-disable max-lines */
import { useContext, useEffect, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import { Fonts, Spacings } from '@styles/index';
import H5 from '@base/H5/H5';
import TextField from '@base/TextField/TextField';
import CountryMenu from '@cont/Login/CountryMenu';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { fetchCountries, verifyMailOtp } from '@actions/auth';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import UnderlineButton from '@base/UnderlineButton/UnderlineButton';
import Login from '@cont/Login/Login';
import {
  fetchTTUQuestions,
  hideTTUWidget,
  postTTUResponse,
  showTTUOption
} from '@actions/ttu';
import imageHelper from '@utils/imageHelper';
import pushRoute from '@utils/pushRoute';
import { TalkState } from '@cont/Login/constants';
import { sendTTUEvents } from '@events/sendAnalytics';
import { TTU_ANSWER_QUESTIONS, TTU_CALL_CONFIRM } from '@constants/segment';
import { authTypes } from '../../../redux/constants';

const Wrapper = styled.div`
  grid-column: span 10;
`;

const DrawerSubTitle = styled(P1)`
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_12B};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const Subheading = styled(H5)`
  margin-bottom: ${Spacings.SPACING_4B};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const TTUInput = styled(TextField)`
  margin-bottom: ${Spacings.SPACING_4B};
  color: ${({ theme }) => theme.colors.fontGreyV2};
`;

const ConfirmButton = styled(Button)`
  margin-top: ${Spacings.SPACING_8B};
`;

const CallImage = styled.img`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const QuestionsTP = styled(Paper)`
  position: relative;
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
`;

const SecondaryColor = styled(P1)`
  color: ${({ theme }) => theme.colors.fontLightGreyV2};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const CreditImage = styled.img`
  position: absolute;
  bottom: 0px;
  right: 0px;
`;

const QuestionHead = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_12B};
  justify-content: space-between;
  align-items: center;
`;

const CreditIcon = styled.img`
  height: 20px;
  width: 20px;
  margin-left: ${Spacings.SPACING_1B};
  margin-right: ${Spacings.SPACING_1B};
  vertical-align: text-bottom;
`;

const QuestionCount = styled(P1)`
  font-size: ${Fonts.LARGE};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const CreditsEarned = styled.span`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const QuestionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
  margin-top: ${Spacings.SPACING_8B};
`;

const OptionsWrapper = styled.div`
  margin-top: ${Spacings.SPACING_4B};
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  border: 1px solid #dde6ed;
  box-sizing: border-box;
  border-radius: 8px;
  margin-bottom: ${Spacings.SPACING_3B};
  cursor: pointer;
`;

const UnselectedCheckbox = styled.div`
  height: 20px;
  width: 20px;
  border-radius: 10px;
  border-color: ${({ theme }) => theme.colors.fontGreyV2};
  border-width: 2px;
  border-style: solid;
  margin-right: ${Spacings.SPACING_2B};
`;

const Selected = styled(UnselectedCheckbox)`
  border-color: ${({ theme }) => theme.colors.green};
  border-width: 6px;
`;

const MyCreditsWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-top: ${Spacings.SPACING_10B};
`;

const CreditsMessage = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.fontGrey};
  margin-right: ${Spacings.SPACING_2B};
`;

const TalkToUnacademy = ({ onClose }) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatchToStore();
  const me = useSelector(({ auth }) => auth.me);
  const showData = useSelector(({ ttu }) => ttu.showData);
  const questionData = useSelector(({ ttu }) => ttu.questions);
  const [name, onNameChange] = useState(me.name);
  const [phone, onPhoneChange] = useState(me.phone);
  const [otp, onOTPChange] = useState('');
  const [a_phone, setAPhone] = useState();
  const [ttuQnNo, setTtuQnNo] = useState(0);
  const [optionSelected, setOptionSelected] = useState(-1);
  const [creditsEarned, addCreditsEarned] = useState(0);
  const [step, setFlowStep] = useState(
    !showData.is_repeat_user && showData.state === TalkState.NO_SHOW
      ? 'call_confirmed'
      : 'phone'
  ); // steps = phone, otp, call_confirmed, questions, done

  const selectedGoal = useSelector(({ auth }) => auth.selectedGoal ?? {});
  const countries = useSelector(({ auth }) => auth.countries ?? []);
  const [country, setCountry] = useState(
    countries.length
      ? countries.find((c) => c.code === 'IN')
      : {
          code: 'IN',
          name: 'India',
          flag_icon_url:
            'https://static.uacdn.net/thumbnail/country-flag-icons/in.png',
          phone_code: '91'
        }
  );

  const changeCountry = (countryOption) => {
    setCountry(countries.find((c) => c.code === countryOption.value));
  };

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchTTUQuestions(selectedGoal?.uid));
  }, [dispatch, selectedGoal?.uid]);

  const phoneNumberQuestion = questionData.find((el) => el.credit === 0) || {};
  const questions = questionData.filter((el) => el.credit > 0);

  const submitAnswer = (optionUid) => {
    let data = {};
    if (!optionUid) {
      data = {
        question_uid: phoneNumberQuestion.uid,
        question_response: phoneNumberQuestion.options[0].uid,
        goal_uid: selectedGoal?.uid
      };
      sendTTUEvents(
        TTU_CALL_CONFIRM,
        {
          user_ttu_status: showData.is_repeat_user ? 'Repeat' : 'New'
        },
        me?.uid,
        me?.username,
        selectedGoal?.uid,
        selectedGoal?.name
      );
      dispatch(hideTTUWidget(TalkState.LOCAL_NO_SHOW));
    } else {
      data = {
        question_uid: questions[ttuQnNo].uid,
        question_response: optionUid,
        goal_uid: selectedGoal?.uid
      };
      addCreditsEarned(creditsEarned + questions[ttuQnNo].credit);
      if (questions.length === ttuQnNo + 1) {
        setOptionSelected(-1);
        setFlowStep('done');
      } else {
        setOptionSelected(-1);
        setTtuQnNo(ttuQnNo + 1);
      }
    }
    dispatch(postTTUResponse(data));
  };

  const handlePhoneConfirmation = (isCallback) => {
    if (isCallback) {
      dispatch(showTTUOption(selectedGoal?.uid)).then((response) => {
        if (response) {
          if (response.result.state === TalkState.SHOW) {
            setFlowStep('call_confirmed');
            submitAnswer();
          } else {
            onClose();
            dispatch(hideTTUWidget(response.result.state));
          }
        }
      });
    }
    if (me.uid) {
      setFlowStep(me.isPhoneVerified || isCallback ? 'call_confirmed' : 'otp');
      submitAnswer();
    }
  };

  const handleOTPConfirmation = async () => {
    if (me.uid && !me.isPhoneVerified) {
      const resp = await dispatch(verifyMailOtp(phone, otp));
      if (resp.type === `${authTypes.VERIFY_MOBILE_OTP}_SUCCESS`) {
        setFlowStep('call_confirmed');
        submitAnswer();
      }
    }
  };

  const handleCurtainClose = () => {
    if (creditsEarned > 0) {
      sendTTUEvents(
        TTU_ANSWER_QUESTIONS,
        {
          user_ttu_status: 'New',
          num_questions_answered: ttuQnNo + 1,
          credits_rewarded: creditsEarned
        },
        me?.uid,
        me?.username,
        selectedGoal?.uid,
        selectedGoal?.name
      );
    }
    onClose();
  };

  function renderNameNumber() {
    return (
      <>
        <H2 color={theme.colors.fontGrey}>Talk to our expert</H2>
        <DrawerSubTitle>
          Experts answer all your questions about the subscription and the goal
        </DrawerSubTitle>
        <Subheading>Your contact details</Subheading>
        <TTUInput
          fullWidth
          value={name}
          type="text"
          placeholder="Enter your name"
          onChange={(e) => onNameChange(e.target.value)}
          autoFocus
        />
        {me.uid ? (
          <>
            <TTUInput
              fullWidth
              startAdornment={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <InputAdornment>
                  <CountryMenu
                    changeCountry={changeCountry}
                    country={country}
                    countries={countries}
                    disabled={me.isPhoneVerified}
                  />
                </InputAdornment>
              }
              placeholder="Enter your mobile number"
              onChange={(e) => onPhoneChange(e.target.value)}
              value={phone}
              disabled={me.isPhoneVerified}
              inputProps={{
                type: 'tel',
                maxLength: 15,
                minLength: 3,
                required: true
              }}
              type="tel"
            />
            <ConfirmButton label="Confirm" onClick={handlePhoneConfirmation} />
          </>
        ) : (
          <Login
            source="ttu"
            ttuUserName={name}
            hideHelp
            loginCallback={() => handlePhoneConfirmation(true)}
          />
        )}
      </>
    );
  }

  function renderOTPInput() {
    return (
      <>
        <H2 color={theme.colors.fontGrey}>Talk to our expert</H2>
        <DrawerSubTitle>
          Experts answer all your questions about the subscription and the goal
        </DrawerSubTitle>
        <H5 color={theme.colors.fontGrey}>
          We have sent an OTP to your mobile number
        </H5>
        {me.uid ? (
          <>
            <TTUInput
              fullWidth
              value={phone}
              type="tel"
              placeholder="Enter your mobile number"
              startAdornment={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <InputAdornment>
                  <CountryMenu
                    changeCountry={changeCountry}
                    country={country}
                    countries={countries}
                    disabled
                  />
                </InputAdornment>
              }
              disabled
            />
            <TTUInput
              fullWidth
              value={otp}
              type="tel"
              autoFocus
              inputProps={{
                type: 'tel',
                maxLength: 6,
                minLength: 4,
                required: true
              }}
              placeholder="Enter OTP"
              onChange={(e) => onOTPChange(e.target.value)}
            />
            <ConfirmButton
              label="Verify"
              onClick={handleOTPConfirmation}
              variant={otp === '' ? 'disabled' : 'filled'}
            />
          </>
        ) : null}
      </>
    );
  }

  function renderCallConfirmed() {
    const totalCredits = questions.reduce((sum, el) => sum + el.credit, 0);
    const showQuestions =
      !showData.is_repeat_user &&
      (showData.state === TalkState.SHOW ||
        showData.state === TalkState.LOCAL_NO_SHOW);
    return (
      <>
        <CallImage src={imageHelper.getImage('call.svg')} alt="call" />
        <H2 color={theme.colors.fontGrey}>Your call is confirmed</H2>
        <DrawerSubTitle>An expert will call you within a day</DrawerSubTitle>
        {showQuestions ? (
          <QuestionsTP>
            <H5 color={theme.colors.fontGrey}>
              {`Answer the following questions and earn ${totalCredits} credits!`}
            </H5>
            <SecondaryColor color="secondary">
              Help the expert understand your learning needs
            </SecondaryColor>
            <CreditImage
              src={imageHelper.getImage('ttu_credit.svg')}
              alt="credits"
            />
            <Button
              maxHeight="sm"
              variant="hollow"
              grid={4}
              label="Answer 5 questions"
              onClick={() => {
                setFlowStep('questions');
              }}
            />
          </QuestionsTP>
        ) : (
          <Button label="Done" onClick={handleCurtainClose} />
        )}
      </>
    );
  }

  function renderQuestions() {
    const currentQuestion = questions[ttuQnNo];
    return (
      <>
        <H5 color={theme.colors.fontGrey}>Follow up questions</H5>
        <QuestionHead>
          <div>
            <CreditIcon
              src={imageHelper.getImage('credit.png')}
              alt="credit_icon"
            />
            <span>
              <CreditsEarned>{`${creditsEarned} `}</CreditsEarned>
              credits
            </span>
          </div>
          <div>
            <QuestionCount>
              {`${ttuQnNo + 1} of ${questions.length}`}
            </QuestionCount>
          </div>
        </QuestionHead>
        <Subheading>{currentQuestion.content}</Subheading>
        {currentQuestion.question_type === 2 ? (
          <TTUInput
            fullWidth
            value={a_phone}
            type="tel"
            placeholder="Alternate number"
            onChange={(e) => {
              const alternateNumber = e.target.value.replace(/ /g, '');
              if (alternateNumber.match(/^\d+$/) || alternateNumber === '') {
                setAPhone(alternateNumber);
              }
            }}
            autoFocus
            inputProps={{
              type: 'tel',
              maxLength: 10,
              minLength: 10,
              required: true
            }}
          />
        ) : (
          <OptionsWrapper>
            {currentQuestion.options.map((option) => (
              <Option
                key={`option-${option.uid}`}
                onClick={() => {
                  setOptionSelected(option.rank);
                  setTimeout(() => {
                    submitAnswer(option.uid);
                  }, 1000);
                }}
              >
                {optionSelected === option.rank ? (
                  <Selected />
                ) : (
                  <UnselectedCheckbox />
                )}
                <P1 color={theme.colors.fontGrey}>{option.content}</P1>
              </Option>
            ))}
          </OptionsWrapper>
        )}
        <QuestionFooter>
          {currentQuestion.question_type === 2 ? (
            <Button
              label="Confirm"
              onClick={() => submitAnswer(a_phone)}
              variant={a_phone === '' ? 'disabled' : 'filled'}
            />
          ) : (
            <div />
          )}
          <UnderlineButton
            label="Skip"
            onClick={
              ttuQnNo + 1 === questions.length
                ? () => setFlowStep('done')
                : () => setTtuQnNo(ttuQnNo + 1)
            }
            color={theme.colors.fontGreyV2}
          />
        </QuestionFooter>
      </>
    );
  }

  function renderCallDone() {
    return (
      <>
        <CallImage src={imageHelper.getImage('call.svg')} alt="call" />
        <H2 color={theme.colors.fontGrey}>You&apos;re all set</H2>
        <DrawerSubTitle>
          Your answers will help the expert understand your needs better. They
          will call you within a day.
        </DrawerSubTitle>
        <MyCreditsWrapper>
          <CreditsMessage>
            You have earned
            <CreditIcon src={imageHelper.getImage('credit.png')} alt="credit" />
            <span>{creditsEarned}</span>
          </CreditsMessage>
          <UnderlineButton
            label="View credits"
            onClick={() => {
              onClose();
              pushRoute('/settings', '/settings');
            }}
            color={theme.colors.green}
          />
        </MyCreditsWrapper>
        <ConfirmButton label="Done" onClick={handleCurtainClose} />
      </>
    );
  }

  return (
    <Wrapper>
      {step === 'phone' && renderNameNumber()}
      {step === 'call_confirmed' && renderCallConfirmed()}
      {step === 'questions' && renderQuestions()}
      {step === 'done' && renderCallDone()}
      {step === 'otp' && renderOTPInput()}
    </Wrapper>
  );
};

export default TalkToUnacademy;

TalkToUnacademy.propTypes = {
  onClose: PropTypes.func.isRequired
};
